import { ErrorBoundary } from 'react-error-boundary';
import { ISearchProduct } from '@monofront/types';
import React, { createContext, useContext } from 'react';

const ProductContext = createContext<ISearchProduct | undefined>(undefined);

export const useProduct = () =>
  useContext<ISearchProduct | undefined>(ProductContext);

export interface IProductCardProps {
  product?: ISearchProduct;
  isInCart: boolean;
  children: React.ReactNode;
}

export const ProductCard = ({
  product,
  isInCart,
  children,
}: IProductCardProps) => {
  return (
    <ErrorBoundary
      fallbackRender={() => (
        <div
          data-testname="product-card"
          className={`tw-mx-auto tw-h-full tw-min-h-[375px] tw-w-full tw-min-w-[172px]
          tw-max-w-[234px] tw-overflow-hidden tw-rounded-md
          tw-border-red-600 tw-bg-red-600/50 tw-p-2 tw-shadow desktop:tw-p-4`}
        ></div>
      )}
      onError={(e) =>
        console.error('An error occurred while rendering the product card', {
          product,
          error: e,
        })
      }
    >
      <ProductContext.Provider value={product}>
        <article
          aria-label={product?.title}
          data-testname="product-card"
          className={`
            tw-grid-template-columns-[2fr_1fr]
            tw-group
            tw-relative tw-mx-auto tw-grid
            tw-h-full
            tw-w-full

            tw-min-w-[172px] tw-max-w-[234px]
            !tw-grid-rows-[max-content_max-content_max-content_max-content_max-content_max-content_max-content_max-content_max-content_2fr_max-content] tw-gap-0 tw-rounded-lg tw-border
            tw-border-solid tw-p-2 tw-grid-template-[badges_wishlist|image_image|pictos_pictos|price_addToCart|bundle_bundle|promotionalOffer_promotionalOffer|format_format|title_title|vendor_vendor|reviews_reviews|notify_notify]
            ${isInCart ? 'tw-border-yellow-600' : 'tw-border-blue-200'}
            tw-bg-white
            tw-font-body

            desktop:tw-p-4
            [&>*:first-child]:tw-mt-2 [&>*:last-child]:tw-mb-0 [&>*]:tw-my-1
            ${product?.inventory_available === false ? 'out-of-stock' : ''}
          `}
        >
          {children}
        </article>
      </ProductContext.Provider>
    </ErrorBoundary>
  );
};

export const ProductCardSlim = ({
  product,
  isInCart,
  children,
}: IProductCardProps) => {
  return (
    <ErrorBoundary
      fallbackRender={() => (
        <div
          data-testname="product-card"
          className={`tw-mx-auto tw-h-full tw-w-[172px]
          tw-overflow-hidden tw-rounded-md tw-border-red-600
          tw-bg-red-600/50 tw-p-2 tw-shadow desktop:tw-w-[210px] desktop:tw-p-4`}
        ></div>
      )}
      onError={(e) =>
        console.error('An error occurred while rendering the product card', {
          product,
          error: e,
        })
      }
    >
      <ProductContext.Provider value={product}>
        <article
          aria-label={product?.title}
          data-testname="product-card"
          className={`
            tw-relative
            tw-mx-auto tw-h-full
            tw-w-[172px] tw-rounded-lg

            tw-border tw-border-solid tw-p-2
            ${isInCart ? 'tw-border-yellow-600' : 'tw-border-blue-200'}
            tw-grid-template-columns-[2fr_1fr] tw-group

            tw-grid !tw-grid-rows-[max-content_max-content_max-content_max-content_max-content_max-content_max-content_max-content_max-content_2fr_max-content] tw-gap-0
            tw-bg-white
            tw-font-body
            tw-grid-template-[badges_wishlist|image_image|pictos_pictos|price_addToCart|bundle_bundle|promotionalOffer_promotionalOffer|format_format|title_title|vendor_vendor|reviews_reviews|notify_notify]

            desktop:tw-w-[210px] desktop:tw-p-4

            [&>*:first-child]:tw-mt-0 [&>*:last-child]:tw-mb-0 [&>*]:tw-my-1
            ${product?.inventory_available === false ? 'out-of-stock' : ''}
        `}
        >
          {children}
        </article>
      </ProductContext.Provider>
    </ErrorBoundary>
  );
};

export const HorizontalProductCard = ({
  product,
  isInCart,
  children,
}: IProductCardProps) => {
  return (
    <ErrorBoundary
      fallbackRender={() => (
        <div
          data-testname="product-card"
          className={`tw-mx-auto
          tw-h-full tw-w-full
          tw-overflow-hidden tw-rounded-lg tw-border-red-600 tw-bg-red-600/50 tw-p-2 tw-shadow desktop:tw-p-4`}
        ></div>
      )}
      onError={(e) =>
        console.error('An error occurred while rendering the product card', {
          product,
          error: e,
        })
      }
    >
      <ProductContext.Provider value={product}>
        <article
          aria-label={product?.title}
          data-testname="product-card"
          className={`
            tw-relative
            tw-h-full
            tw-w-full tw-rounded-lg
            tw-border tw-border-solid tw-p-3
            ${isInCart ? 'tw-border-yellow-600' : 'tw-border-blue-200'}
            tw-group tw-grid

            !tw-grid-cols-[max-content_2fr_max-content] !tw-grid-rows-[min-content_max-content_max-content_max-content_max-content_max-content_2fr_min-content]
            tw-bg-white tw-font-body
            tw-grid-template-[image_badges_auto|image_vendor_auto|image_title_auto|image_format_auto|image_bundle_auto|image_promotionalOffer_auto|image_price_addToCart|image_pfand_pfand|notify_notify_notify]

            md:tw-p-4
            [&>*:first-child]:tw-mr-4
            [&>*]:tw-mb-1
            [&>.addToCart]:!tw-mb-0 [&>.addToCart]:tw-self-end
            [&>.productPrice]:tw-h-11 [&>.productPrice]:!tw-self-end
            [&>.wishlist]:tw-justify-self-center

            ${product?.inventory_available === false ? 'out-of-stock' : ''}
          `}
        >
          {children}
        </article>
      </ProductContext.Provider>
    </ErrorBoundary>
  );
};

export const SideCartHorizontalProductCard = ({
  product,
  children,
}: Omit<IProductCardProps, 'isInCart'>) => {
  return (
    <ErrorBoundary
      fallbackRender={() => (
        <div
          data-testname="product-card"
          className={`tw-mx-auto
          tw-w-full tw-overflow-hidden
          tw-rounded-lg tw-border-red-600 tw-bg-red-600/50 tw-p-2 tw-shadow desktop:tw-p-4`}
        ></div>
      )}
      onError={(e) =>
        console.error('An error occurred while rendering the product card', {
          product,
          error: e,
        })
      }
    >
      <ProductContext.Provider value={product}>
        <article
          aria-label={product?.title}
          data-testname="product-card"
          className={`
            tw-group
            tw-relative tw-grid tw-w-full
            !tw-grid-cols-[max-content_1fr_max-content_56px]

            tw-px-1 tw-py-4
            tw-font-body
            tw-grid-template-[image_badges_badges_wishlist|image_vendor_vendor_auto|image_title_title_auto|image_format_format_auto|image_bundle_bundle_auto|image_promotionalOffer_promotionalOffer_auto|image_price_addToCart_addToCart|image_pfand_pfand_pfand|notify_notify_notify_notify]

            [&>*:first-child]:tw-mr-2 [&>*]:tw-mb-1
            [&>.addToCart]:!tw-mb-0
            [&>.wishlist>button]:tw-p-2 [&>.wishlist]:tw-w-auto
            ${product?.inventory_available === false ? 'out-of-stock' : ''}
          `}
        >
          {children}
        </article>
      </ProductContext.Provider>
    </ErrorBoundary>
  );
};

export const HorizontalProductCardSlim = ({
  product,
  children,
}: Omit<IProductCardProps, 'isInCart'>) => {
  return (
    <ErrorBoundary
      fallbackRender={() => (
        <div
          data-testname="product-card"
          className={`tw-mx-auto
           tw-overflow-hidden tw-rounded-lg tw-border-red-600
          tw-bg-red-600/50 tw-p-2 tw-shadow desktop:tw-p-4`}
        ></div>
      )}
      onError={(e) =>
        console.error('An error occurred while rendering the product card', {
          product,
          error: e,
        })
      }
    >
      <ProductContext.Provider value={product}>
        <article
          aria-label={product?.title}
          data-testname="product-card"
          className="tw-w-full
          tw-bg-white tw-p-4
            tw-font-body"
        >
          <div
            className={`
            tw-relative
            tw-grid
            !tw-grid-cols-[max-content_2fr_max-content_max-content_80px]
            tw-grid-template-[image_badges_wishlist_addToCart_totalPrice|image_vendor_wishlist_addToCart_totalPrice|image_title_wishlist_addToCart_totalPrice|image_format_wishlist_addToCart_totalPrice|image_bundle_wishlist_addToCart_totalPrice|image_promotionalOffer_wishlist_addToCart_totalPrice|image_price_wishlist_addToCart_totalPrice]
            [&>*:first-child]:tw-mx-0 [&>*:first-child]:tw-mt-0 [&>*:last-child]:!tw-mr-0 [&>*]:tw-mx-4 [&>*]:tw-mb-1 [&>.productPrice]:tw-mt-1 [&>.totalPrice]:tw-self-center [&>.wishlist]:tw-flex [&>.wishlist]:tw-items-center [&>.wishlist]:tw-self-center
            `}
          >
            {children}
          </div>
        </article>
      </ProductContext.Provider>
    </ErrorBoundary>
  );
};
